/**
 * Cola OFFLINE de aportes (hito M1 · PWA en wifi hostil).
 *
 * El Bolsillo debe poder aportar SIN conexión: el aporte se persiste en
 * localStorage y se reintenta al volver la red. La seguridad del reintento la
 * da la IDEMPOTENCIA de contrato: cada aporte lleva en su payload jsonb un
 * token `idem` (uuid del cliente). El backend, antes de insertar, busca un
 * aporte de esa actividad con payload->>'idem' == token; si existe lo devuelve
 * (200) en vez de duplicar. Así respetamos C1 (INSERT-only) y los reintentos
 * son seguros aunque el POST original sí hubiera llegado.
 *
 * Diseño:
 *   · Store zustand para que el contador de pendientes sea reactivo en la UI.
 *   · Persistencia manual a localStorage (control total del formato/versionado).
 *   · `enviarAporte` intenta el POST directo; si falla por RED encola; si falla
 *     por un error DEFINITIVO del servidor (4xx) lo propaga a la UI.
 *   · `flush` se dispara solo con el evento 'online' del navegador y desde
 *     useSalaViva al recuperar el WS en vivo.
 */
import { create } from 'zustand';
import { sesionApi, type Aporte, type AporteTipo } from './sesion';
import { ApiError } from './api';

const LS_KEY = 'agora:cola-aportes:v1';

/** Un aporte pendiente de envío, con su token de idempotencia. */
export interface AporteEnCola {
  /** uuid del cliente; viaja dentro de payload.idem (dedup de contrato). */
  idem: string;
  actividadId: string;
  tipo: AporteTipo;
  /** payload jsonb ya con la clave `idem` incluida. */
  payload: Record<string, unknown>;
  /** Epoch ms de encolado (para orden y diagnóstico). */
  ts: number;
  /** Reintentos consumidos (diagnóstico; no limita el reintento). */
  intentos: number;
}

/** uuid v4 con reserva por si crypto.randomUUID no está disponible. */
export function nuevoIdem(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Reserva suficiente para un token de deduplicación de cliente.
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function cargar(): AporteEnCola[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as AporteEnCola[]) : [];
  } catch {
    return [];
  }
}

function guardar(items: AporteEnCola[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {
    /* cuota / modo privado: la cola vive al menos en memoria esta sesión */
  }
}

interface ColaState {
  items: AporteEnCola[];
  vaciando: boolean;
  /** Añade (o reemplaza por idem) un aporte a la cola y persiste. */
  encolar: (item: AporteEnCola) => void;
  /** Quita un aporte por su token idem. */
  quitar: (idem: string) => void;
  /** Reintenta todos los pendientes en orden; se detiene ante fallo de red. */
  flush: () => Promise<void>;
}

export const useColaStore = create<ColaState>((set, get) => ({
  items: cargar(),
  vaciando: false,

  encolar: (item) =>
    set((s) => {
      const sinDup = s.items.filter((i) => i.idem !== item.idem);
      const items = [...sinDup, item];
      guardar(items);
      return { items };
    }),

  quitar: (idem) =>
    set((s) => {
      const items = s.items.filter((i) => i.idem !== idem);
      guardar(items);
      return { items };
    }),

  flush: async () => {
    if (get().vaciando) return;
    if (get().items.length === 0) return;
    set({ vaciando: true });
    try {
      // Copia estable: procesamos en orden de encolado.
      for (const item of [...get().items]) {
        try {
          await sesionApi.postAporte(item.actividadId, {
            tipo: item.tipo,
            payload: item.payload,
          });
          // Éxito (o dedup 200): fuera de la cola.
          get().quitar(item.idem);
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) {
            // Fallo de RED: seguimos offline; dejamos el resto y cortamos.
            set((s) => ({
              items: s.items.map((i) =>
                i.idem === item.idem ? { ...i, intentos: i.intentos + 1 } : i,
              ),
            }));
            break;
          }
          if (err instanceof ApiError && err.status >= 500) {
            // Fallo transitorio del servidor: reintentar luego, sin bloquear.
            set((s) => ({
              items: s.items.map((i) =>
                i.idem === item.idem ? { ...i, intentos: i.intentos + 1 } : i,
              ),
            }));
            continue;
          }
          // Error DEFINITIVO (4xx: actividad cerrada, validación…): no se puede
          // entregar; lo sacamos para no reintentar en bucle.
          get().quitar(item.idem);
        }
      }
    } finally {
      set({ vaciando: false });
    }
  },
}));

/** Resultado de un intento de envío desde la UI. */
export type ResultadoEnvio =
  | { estado: 'enviado'; aporte: Aporte }
  | { estado: 'en_cola'; idem: string };

/**
 * Envía un aporte con idempotencia y respaldo offline.
 * - Genera (o respeta) un token `idem` dentro del payload.
 * - Si hay red, intenta el POST directo y devuelve el aporte creado.
 * - Si falla por red/offline, lo encola y devuelve estado 'en_cola'.
 * - Un error definitivo del servidor (p. ej. 409 actividad cerrada) se propaga
 *   para que la UI lo muestre; no se encola.
 */
export async function enviarAporte(
  actividadId: string,
  tipo: AporteTipo,
  payloadBase: Record<string, unknown>,
): Promise<ResultadoEnvio> {
  const idem =
    typeof payloadBase.idem === 'string' && payloadBase.idem ? payloadBase.idem : nuevoIdem();
  const payload = { ...payloadBase, idem };

  const encolar = (): ResultadoEnvio => {
    useColaStore.getState().encolar({
      idem,
      actividadId,
      tipo,
      payload,
      ts: Date.now(),
      intentos: 0,
    });
    return { estado: 'en_cola', idem };
  };

  // Sin red conocida: directo a la cola (evita un timeout inútil).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return encolar();
  }

  try {
    const aporte = await sesionApi.postAporte(actividadId, { tipo, payload });
    return { estado: 'enviado', aporte };
  } catch (err) {
    if (err instanceof ApiError && err.status === 0) {
      return encolar();
    }
    throw err;
  }
}

// Reintento automático al recuperar la red. Registro único a nivel de módulo.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void useColaStore.getState().flush();
  });
}
